import { Link, Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Server, LogOut, Loader2 } from 'lucide-react';

const navLink = ({ isActive }) =>
  `rounded-md px-2 py-1 text-sm ${
    isActive
      ? 'bg-neutral-100 font-medium text-neutral-900 dark:bg-neutral-700 dark:text-white'
      : 'text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white'
  }`;
import { usePlatformAuthStore } from '../../context/platformAuthStore';

/**
 * The operator console's shell. Deliberately not DashboardLayout: that one
 * is a tenant user's navigation, filtered by a tenant role's permissions,
 * and an operator has neither. One header, one list, no sidebar.
 */
export default function ManageLayout() {
  const { isAuthenticated, isInitializing, operator, logout } = usePlatformAuthStore();
  const location = useLocation();

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/manage/login" replace state={{ from: location.pathname }} />;
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <header className="border-b border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <Link to="/manage" className="flex items-center gap-2 text-neutral-900 dark:text-white">
              <Server className="h-5 w-5 text-primary-600" />
              <span className="font-semibold">Platform console</span>
            </Link>
            <nav className="flex items-center gap-1">
              <NavLink to="/manage" end className={navLink}>
                Tenants
              </NavLink>
              <NavLink to="/manage/releases" className={navLink}>
                Releases
              </NavLink>
              <NavLink to="/manage/setup" className={navLink}>
                Setup
              </NavLink>
            </nav>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-neutral-600 dark:text-neutral-300">
              {operator?.name} <span className="text-neutral-400">({operator?.email})</span>
            </span>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
